import { useEffect, useState } from 'react';
import { maxOfferService } from '../services/maxOfferService';
import type { MaxOffer } from '../services/maxOfferService';

/**
 * The live Max offer for a promo code, plan and seat count.
 *
 * Debounced, because the promo field is a text input and the endpoint behind
 * this is rate limited at 120 requests per 15 minutes per IP: a call per
 * keystroke would burn a visitor's whole budget typing one code.
 *
 * The previous offer is held while a new one is in flight, so the price on
 * screen never blanks out mid-edit. A failed call leaves the last good offer
 * in place: the endpoint answering badly must not make a valid code look
 * invalid.
 */
export function useMaxOffer({
  promoCode,
  trialCode,
  plan = 'monthly',
  seats = 1,
  enabled = true,
  debounceMs = 400,
}: {
  promoCode?: string;
  trialCode?: string;
  plan?: 'monthly' | 'annual';
  seats?: number;
  enabled?: boolean;
  debounceMs?: number;
}): { offer: MaxOffer | null; loading: boolean } {
  const [offer, setOffer] = useState<MaxOffer | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      maxOfferService
        .getOffer({ promoCode, trialCode, plan, seats })
        .then((next) => {
          if (cancelled) return;
          if (next) setOffer(next);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [promoCode, trialCode, plan, seats, enabled, debounceMs]);

  return { offer, loading };
}
